import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { XMarkIcon, ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import { useData } from '../context/DataContext.js';

export default function AddVenue({ isOpen, onClose, onVenueAdded, onVenueSelected, selectedStateId }) {
  const { states, venues, refreshVenues } = useData();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [existingVenue, setExistingVenue] = useState(null);
  
  const [formData, setFormData] = useState({
    venueName: '',
    addressLine: '',
    city: '',
    stateId: selectedStateId || '',
    contactPhone: '',
    contactEmail: ''
  });

  // Get the selected state name
  const selectedState = states.find(state => state.StateID.toString() === selectedStateId?.toString());

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        venueName: '',
        addressLine: '',
        city: '',
        stateId: selectedStateId || '',
        contactPhone: '',
        contactEmail: ''
      });
      setError('');
      setExistingVenue(null);
    }
  }, [isOpen, selectedStateId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      // Check if venue exists in context
      const existingVenue = venues.find(
        v => v.VenueName.toLowerCase() === formData.venueName.toLowerCase().trim() 
        && parseInt(v.StateID) === parseInt(formData.stateId)
      );
      
      if (existingVenue) {
        setExistingVenue(existingVenue);
        setError('A venue with this name already exists in the selected state.');
        setIsLoading(false);
        return;
      }

      const requestBody = {
        VenueName: formData.venueName,
        AddressLine: formData.addressLine,
        City: formData.city,
        StateID: formData.stateId,
        CountryID: 1,
        ContactPhone: formData.contactPhone,
        ContactEmail: formData.contactEmail
      };

      console.log('Creating venue with data:', {
        url: 'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/venues',
        method: 'POST',
        body: requestBody
      });

      const response = await fetch(
        'https://r7ej0e9i28.execute-api.ap-southeast-2.amazonaws.com/dev/venues',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestBody)
        }
      );

      console.log('Response status:', response.status);
      const responseData = await response.json();
      console.log('Response data:', responseData);

      if (!response.ok) {
        throw new Error('Failed to create venue');
      }

      await refreshVenues();
      if (onVenueAdded) {
        onVenueAdded();
      }
      onClose();
    } catch (err) {
      console.error('Error creating venue:', err);
      setError('Failed to create venue. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Add this function to use existing venue
  const handleUseExisting = () => {
    if (onVenueSelected) {
      onVenueSelected(existingVenue);
    }
    if (onVenueAdded) {
      onVenueAdded();
    }
    onClose();
  };

  if (!isOpen) return null;

  if (!selectedStateId) {
    return createPortal(
      <div className="fixed inset-0 z-[230] flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
        <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
          <p className="text-center text-gray-700">Please select a state first</p>
          <button
            type="button"
            onClick={onClose}
            className="mt-4 w-full rounded-md bg-slate-900 px-3 py-2 text-sm font-semibold text-white hover:bg-slate-700"
          >
            Close
          </button>
        </div>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div className="fixed inset-0 z-[230] flex items-center justify-center bg-gray-500 bg-opacity-75 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between px-4 py-4 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Add New Venue</h2>
          <div className="flex items-center space-x-2">
            <button
              type="button"
              disabled={isLoading}
              onClick={handleSubmit}
              className="flex flex-col items-center px-2 text-slate-900 hover:text-slate-700 disabled:text-slate-400"
            >
              <ArrowUpTrayIcon className="h-6 w-6" />
              <span className="text-[10px]">Save</span>
            </button>
            <button
              onClick={onClose}
              className="flex flex-col items-center px-2 text-gray-500 hover:text-gray-700"
            >
              <XMarkIcon className="h-6 w-6" />
              <span className="text-[10px]">Close</span>
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 bg-gray-50">
          {error && existingVenue ? (
            <div className="mb-4 space-y-4">
              <div className="text-sm text-red-600 text-center">
                {error}
              </div>
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-medium text-gray-900">Existing Venue Details:</h3>
                <p className="text-sm text-gray-600 mt-1">
                  {existingVenue.VenueName}<br />
                  {existingVenue.City}
                </p>
                <button
                  type="button"
                  onClick={handleUseExisting}
                  className="mt-3 w-full rounded-md bg-slate-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-slate-700"
                >
                  Use Existing Venue
                </button>
              </div>
            </div>
          ) : error ? (
            <div className="mb-4 text-sm text-red-600 text-center">
              {error}
            </div>
          ) : null}

          <div className="space-y-4">
            {/* Venue Name */}
            <div>
              <label htmlFor="venueName" className="block text-sm font-medium text-gray-700">
                Venue Name *
              </label>
              <input
                type="text"
                id="venueName"
                required
                value={formData.venueName}
                onChange={(e) => setFormData({ ...formData, venueName: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            {/* Address Line */}
            <div>
              <label htmlFor="addressLine" className="block text-sm font-medium text-gray-700">
                Address Line *
              </label>
              <input
                type="text"
                id="addressLine"
                required
                value={formData.addressLine}
                onChange={(e) => setFormData({ ...formData, addressLine: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            {/* City */}
            <div>
              <label htmlFor="city" className="block text-sm font-medium text-gray-700">
                City *
              </label>
              <input
                type="text"
                id="city"
                required
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            {/* State - Show as read-only if state is pre-selected */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                State *
              </label>
              {selectedStateId ? (
                <p className="mt-1 text-sm text-gray-600">
                  {selectedState?.StateNameShort || 'Unknown State'}
                </p>
              ) : (
                <select
                  id="state"
                  required
                  value={formData.stateId}
                  onChange={(e) => setFormData({ ...formData, stateId: e.target.value })}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
                >
                  <option value="">Select a state</option>
                  {states.map(state => (
                    <option key={state.StateID} value={state.StateID}>
                      {state.StateNameShort}
                    </option>
                  ))}
                </select>
              )}
            </div>

            {/* Contact Information */}
            <div>
              <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                Contact Phone
              </label>
              <input
                type="tel"
                id="contactPhone"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>

            <div>
              <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                Contact Email
              </label>
              <input
                type="email"
                id="contactEmail"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-slate-500 focus:ring-slate-500"
              />
            </div>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
} 